import { useQuery } from '@tanstack/react-query';
import { ListIcon, Plus, Search, XIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';

import type { Pagination } from '@/common/pagination';
import { ROLE } from '@/common/role';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDebounceValue } from '@/hooks/use-debounce-value';
import { usePermissionMetadata } from '@/hooks/use-permission';
import { type AssetListQueryFilterType, getListAssets } from '@/queries/assets/list-assets';
import { getListOrganizationsOption } from '@/queries/organizations/list-organizations';
import type { ProfileLoaderType } from '@/routes/loader/profile';
import type { AssetWithStats } from '../../../common/asset';
import { cn } from '../../../lib/utils';
import { SelectOrganizationPopover } from '../../Organizations/SelectOrganizationPopup';
import { AssetListMapCard } from './map/AssetListMapCard';
import { AssetsMapContainer } from './map/AssetsMapContainer';

export function AssetsPageV2() {
	const { t } = useTranslation();
	const { profile } = (useLoaderData() as ProfileLoaderType) || {};
	const [searchTerm, setSearchTerm] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useDebounceValue(searchTerm, 500);
	const [organizationFilter, setOrganizationFilter] = useState<string>('all');
	const [filters, setFilters] = useState({} as AssetListQueryFilterType);
	const [showSidePanel, setShowSidePanel] = useState(true);
	const [pagination, _setPagination] = useState({
		limit: 100,
		offset: 0,
	} as Pagination);
	const { isSuperAdmin } = usePermissionMetadata();
	const { data: assetsResponse, isLoading } = useQuery({
		queryKey: ['assets', 'list', filters, pagination],
		queryFn: () => getListAssets({ ...filters, ...pagination }),
		enabled: !!profile,
	});
	const { data: orgListResponse } = useQuery(getListOrganizationsOption({ enabled: isSuperAdmin }));
	const organizations = orgListResponse?.organizations || [];
	const navigate = useNavigate();
	const assets = assetsResponse?.assets || [];
	const [selectedAsset, setSelectedAsset] = useState<AssetWithStats | null>(null);
	const listCardRef = useRef<Record<string, HTMLDivElement | null>>({});

	useEffect(() => {
		const newFilters: AssetListQueryFilterType = {};
		if (debouncedSearch) {
			newFilters.search = debouncedSearch;
		}
		if (organizationFilter !== 'all') {
			newFilters.organizationId = organizationFilter;
		}
		setFilters(newFilters);
	}, [debouncedSearch, organizationFilter]);

	const handleSearchTermInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchTerm(value);
		setDebouncedSearch(value);
	};

	const onSelectAsset = useCallback((asset: AssetWithStats) => {
		setSelectedAsset(asset);
		listCardRef.current[asset.id || '']?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, []);

	const toggleSidePanel = useCallback(() => {
		setShowSidePanel((prev) => !prev);
	}, []);

	return (
		<div className={cn('w-full mx-auto flex flex-col relative h-[calc(100vh-64px)]')}>
			{/* Side Panel Container */}
			<div
				className={cn('absolute top-2 left-2 w-110 z-20 flex flex-col transition-all', showSidePanel ? 'bottom-4' : '')}
			>
				{/* Top Row: Search Input - Toggle Button */}
				<div className="flex items-center gap-2 w-full z-10">
					<div className="relative flex-1">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
						<Input
							type="text"
							className="flex h-10 w-full rounded-md border pl-9 bg-white/95 backdrop-blur-sm shadow-sm"
							placeholder={t('assets.list.searchPlaceholder')}
							value={searchTerm}
							onChange={handleSearchTermInput}
							autoComplete="off"
						/>
					</div>
					<Button
						variant="outline"
						size="icon"
						className="shrink-0 h-10 w-10 bg-white/90 backdrop-blur-sm hover:bg-white shadow-sm cursor-pointer"
						onClick={toggleSidePanel}
						title={showSidePanel ? t('assets.map.closeAssetList') : t('assets.map.openAssetList')}
					>
						{showSidePanel ? <XIcon className="size-4" /> : <ListIcon className="size-4" />}
					</Button>
				</div>

				{/* Asset List Container (Frosted Glass Effect & Bottom Space) */}
				<div
					className={cn(
						'flex-1 mt-2 overflow-hidden rounded-lg shadow-lg border border-zinc-200/50 bg-white/60 backdrop-blur-md transition-all duration-300',
						{ hidden: !showSidePanel },
					)}
				>
					<div className="h-full w-full overflow-y-auto p-2 space-y-2">
						{assets.map((asset) => (
							<AssetListMapCard
								ref={(ele) => {
									listCardRef.current[asset.id || ''] = ele;
								}}
								key={asset.id}
								asset={asset}
								onSelectAsset={onSelectAsset}
								selectedAssetId={selectedAsset?.id}
							/>
						))}
					</div>
				</div>
			</div>

			{/* Filters / Actions */}
			<div className="flex flex-row absolute top-2 left-120 w-fit z-10 gap-4">
				{isSuperAdmin && (
					<div className="relative w-auto min-w-70 max-h-fit flex-1">
						<SelectOrganizationPopover
							asFilter
							addAllItem
							inactiveToggle
							organizations={organizations}
							onChange={(value) => setOrganizationFilter(value)}
							value={organizationFilter}
						/>
					</div>
				)}
				<Restricted to={ROLE.ASSETS_CREATE}>
					<Button
						className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-sm"
						onClick={() => navigate('/assets/new')}
					>
						<Plus className="w-4 h-4" />
						{t('assets.list.createNewAsset')}
					</Button>
				</Restricted>
			</div>

			{/* Main Map Background Container */}
			<div className="absolute top-0 left-0 bottom-0 flex flex-row h-full w-full border-2 border-zinc-300 rounded-lg mb-2">
				<div className="flex-1 flex m-0 h-full w-full">
					<AssetsMapContainer
						assets={assets}
						selectedAsset={selectedAsset}
						onSelectAsset={onSelectAsset}
						showSidePanel={showSidePanel}
						leftPanelWidth={440}
					/>
				</div>
			</div>
		</div>
	);
}
